import React, {useContext, useEffect, useState} from "react";
import '../assets/css/Dentobook.css';
import DetailWrapper from "../components/common/DetailWrapper";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import ReportsSearchForm from "../components/reports/ReportsSearchForm";
import {Button, Spin, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {DownloadOutlined} from "@ant-design/icons";
import {isMobileOnly} from "react-device-detect";
import InfiniteScroll from "react-infinite-scroll-component";
import UserIcon from "../components/common/UserIcon";
import {MAX_PATIENTS_PER_PAGE} from "../components/common/Constants";
import {
    MedicalStaff,
    StatusResponse,
    Treatment,
    Patient,
    PatientListResponse,
    ClinicResponse
} from "../api-client";
import dayjs, {Dayjs} from "dayjs";
import {Link, useNavigate} from "react-router-dom";
import {getReportsApi, getMedicalStaffApi, getPatientApi, getTreatmentsApi, getStatusesApi, getClinicsApi} from "../api/apiInstances";
import logger from "../utils/logger";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {useTrackEvent} from "../hooks/useTrackEvent";

// Report type based on API response structure (ReportsPaginationResponse returns Array<object>)
interface Report {
    patient: {
        id: string;
        firstName: string;
        lastName: string;
        avatar?: string;
    };
    date: string;
    conditionId: string;
    conditionDisplayName?: string;  // Locale-resolved display name (Phase 2 fix)
    treatmentId: string;
    treatmentDisplayName?: string;  // Locale-resolved display name (Phase 2 fix)
    toothNumber: number | string;
    price: number;
    debt: number;
    paid: number;
    doctorId: string;
}

const Reports = () => {

    const navigate = useNavigate();
    const intl = useIntl();
    const {userOrganization} = useContext(DentobookApplicationContext);

    const [loading, setLoading] = useState<boolean>(true)
    const [height, setHeight] = useState<number>(window.innerHeight);
    const [fromDate, setFromDate] = useState<Dayjs>(dayjs());
    const [toDate, setToDate] = useState<Dayjs>(dayjs());
    const [patient, setPatient] = useState<string>('');
    const [doctor, setDoctor] = useState<string>('');
    const [clinic, setClinic] = useState<string>('');  // '' = all clinics
    const [clinics, setClinics] = useState<ClinicResponse[]>([]);
    const track = useTrackEvent();
    const [total, setTotal] = useState<number>(0);
    const [allPages, setAllPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [reports, setReports] = useState<Report[]>([]);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [totalDebt, setTotalDebt] = useState<number>(0);
    const [totalPaid, setTotalPaid] = useState<number>(0);
    const [statusesMap, setStatusesMap] = useState<Map<string, StatusResponse>>(new Map());
    const [treatmentsMap, setTreatmentsMap] = useState<Map<string, Treatment>>(new Map());
    const [medicalStaffMap, setMedicalStaffMap] = useState<Map<string, MedicalStaff>>(new Map());
    const [medicalStaff, setMedicalStaff] = useState<MedicalStaff[]>([]);
    const [patients, setPatients] = useState<PatientListResponse[]>([]);

    const reportsApi = getReportsApi();
    const medicalApi = getMedicalStaffApi();
    const patientApi = getPatientApi();
    const treatmentsApi = getTreatmentsApi();
    const statusesApi = getStatusesApi();
    const clinicsApi = getClinicsApi();

    const addZeroToNumber = (number: string) => {
        if (+number < 10) {
            number = '0' + number;
        }

        return number;
    };

    const loadReports = async (page: number) => {
        setLoading(true);
        const fromDateString = fromDate.year() + '-' + addZeroToNumber((fromDate.month() + 1).toString()) + '-' + addZeroToNumber(fromDate.date().toString());
        const toDateString = toDate.year() + '-' + addZeroToNumber((toDate.month() + 1).toString()) + '-' + addZeroToNumber(toDate.date().toString());
        const reportsData = await reportsApi.fetchReports(fromDateString, toDateString, doctor, patient, clinic, page, 100);
        setTotal(reportsData.data.totalElements!);
        setAllPages(reportsData.data.pagesCount!);
        const typedReports: Report[] = reportsData.data.reports!.map((r: any): Report => ({
            patient: r.patient,
            date: r.date,
            conditionId: r.conditionId,
            conditionDisplayName: r.conditionDisplayName,  // Phase 2: locale-resolved name
            treatmentId: r.treatmentId,
            treatmentDisplayName: r.treatmentDisplayName,  // Phase 2: locale-resolved name
            toothNumber: r.toothNumber,
            price: r.price,
            debt: r.debt,
            paid: r.paid,
            doctorId: r.doctorId
        }));

        let allReports: Report[];
        if (page === 0) {
            setReports(typedReports);
            allReports = typedReports;
        } else {
            allReports = reports.concat(typedReports);
            setReports(allReports);
        }

        // Calculate totals for all loaded reports
        const totals = allReports.reduce(
            (acc, report) => ({
                price: acc.price + (report.price || 0),
                debt: acc.debt + (report.debt || 0),
                paid: acc.paid + (report.paid || 0)
            }),
            { price: 0, debt: 0, paid: 0 }
        );

        setTotalPrice(totals.price);
        setTotalDebt(totals.debt);
        setTotalPaid(totals.paid);
        setLoading(false);
    }

    const loadData = async () => {
        setLoading(true);
        const patientsData = await patientApi.fetchAllPatients('', 'id', 'asc', 0, MAX_PATIENTS_PER_PAGE);
        setPatients(patientsData.data!.patients!);
        const statusesData = await statusesApi.fetchAllStatuses();
        let map = new Map();
        for (const item of statusesData.data) {
            map.set(item.id, item);
        }
        setStatusesMap(map);
        map = new Map();
        const treatmentsData = await treatmentsApi.fetchMedicalTreatments();
        for (const item of treatmentsData.data) {
            map.set(item.id, item);
        }
        setTreatmentsMap(map);
        map = new Map();
        const medicalStaffData = await medicalApi.fetchAllMedicalStaff("", "id", 1, 10000);
        setMedicalStaff(medicalStaffData.data.medicalStaff!);
        for (const item of medicalStaffData.data.medicalStaff!) {
            map.set(item.id, item);
        }
        setMedicalStaffMap(map);
        const clinicsData = await clinicsApi.listClinics();
        setClinics(clinicsData.data ?? []);
    };

    useEffect(() => {
        setHeight(window.innerHeight * 0.01);
        const handleResize = () => {
            setHeight(window.innerHeight * 0.01);
        };
        window.addEventListener('resize', handleResize);
        loadData();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setCurrentPage(0);
        loadReports(0);
    }, [fromDate, toDate, patient, doctor, clinic]);

    const handleCsvGeneration = async () => {
        setLoading(true);
        try {
            const fromDateString = fromDate.year() + '-' + addZeroToNumber((fromDate.month() + 1).toString()) + '-' + addZeroToNumber(fromDate.date().toString());
            const toDateString = toDate.year() + '-' + addZeroToNumber((toDate.month() + 1).toString()) + '-' + addZeroToNumber(toDate.date().toString());

            // Download with authentication (same pattern as invoice PDF downloads)
            const response = await reportsApi.generateReportsDocument(
                fromDateString,
                toDateString,
                doctor,
                patient,
                clinic,
                { responseType: 'blob' }
            );

            // Create blob and trigger download
            const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `reports_${fromDateString}_${toDateString}.xls`;
            link.click();
            track('report_exported', {
                has_patient_filter: patient !== '',
                has_doctor_filter: doctor !== '',
                has_clinic_filter: clinic !== '',
            });
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            logger.error(error);
        } finally {
            setLoading(false);
        }
    };

    const tableFooter = () => (
        <div className="dentobook-operations-table-footer" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: '#fafafa',
            borderTop: '1px solid #f0f0f0'
        }}>
            <div style={{fontSize: '12px', color: '#666'}}>
                <div><FormattedMessage id="currency"/>: {userOrganization?.currency ?? 'EUR'}</div>
                <div><FormattedMessage id="vat_excluded"/></div>
            </div>
            <div style={{display: 'flex', gap: '24px', fontWeight: 700}}>
                <span>
                    <FormattedMessage id="price"/>: {totalPrice.toFixed(2)} {userOrganization?.currency ?? 'EUR'}
                </span>
                <span>
                    <FormattedMessage id="debt_label"/>: {totalDebt.toFixed(2)} {userOrganization?.currency ?? 'EUR'}
                </span>
                <span>
                    <FormattedMessage id="paidFinance"/>: {totalPaid.toFixed(2)} {userOrganization?.currency ?? 'EUR'}
                </span>
            </div>
        </div>
    );

    return (
        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
            <DetailWrapper>
                <div className="reports-header">
                    <div>
                        <h3>
                            <FormattedMessage id="reports_label"/>
                        </h3>
                    </div>
                    <div>
                        <Button onClick={handleCsvGeneration} className="rounded-btn" disabled={loading}>
                            <DownloadOutlined/> <FormattedMessage id="download_report"/>
                        </Button>
                    </div>
                </div>
                <ReportsSearchForm fromDate={fromDate} setFromDate={setFromDate}
                                   toDate={toDate} setToDate={setToDate}
                                   patient={patient} setPatient={setPatient}
                                   doctor={doctor} setDoctor={setDoctor}
                                   clinic={clinic} setClinic={setClinic}
                                   patients={patients} medicalStaff={medicalStaff}
                                   clinics={clinics}
                />

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin tip={intl.formatMessage({ id: 'loading' })} />
                    </div>
                ) : (
                    <>
                        {isMobileOnly ? <div className="horizontal-scroll-indicator"></div> : null}
                        <div id="scrollableDiv" style={{
                            height: `${height * 80 - (!isMobileOnly ? 170 : 300)}px`,
                            width: isMobileOnly ? '90%' : '97%',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            position: 'absolute'
                        }}>
                            <InfiniteScroll
                                scrollableTarget="scrollableDiv"
                                dataLength={reports.length}
                                next={async () => {
                                    await loadReports(currentPage + 1);
                                    setCurrentPage(currentPage + 1);
                                }}
                                hasMore={reports.length < total}
                                style={{overflow: 'hidden'}}
                                loader={<div style={{ textAlign: 'center', padding: '20px' }}><Spin size="small" /></div>}
                                refreshFunction={() => {
                                }}
                                pullDownToRefresh
                            >
                                <Table dataSource={reports}
                                       pagination={false}
                                       locale={{emptyText: intl.formatMessage({id: 'no_data'})}}
                                       className="dentobook-table dentobook-scrollable-table"
                                       footer={tableFooter}
                                       onRow={(report) => {
                                           return {
                                               onClick: () => {
                                                   navigate('/patients/' + report.patient!.id + '?tab=medical-history&subTab=treatment&redirectedFrom=reports');
                                               }
                                           };
                                       }}>
                                    <Table.Column key="patient" title={<FormattedMessage id="patient"/>}
                                                  width="10%"
                                                  sorter={reports.length ? (reportOne: Report, reportTwo: Report) => {
                                                      const fullNameOne = reportOne.patient!.firstName + ' ' + reportOne.patient!.lastName;
                                                      const fullNameTwo = reportTwo.patient!.firstName + ' ' + reportTwo.patient!.lastName;

                                                      return fullNameOne.localeCompare(fullNameTwo);
                                                  } : undefined}
                                                  render={(report) => {
                                                      const avatarSrc = report.patient.avatarUrl;
                                                      return (
                                                          <div className="username-icon">
                                                              {!avatarSrc ? <UserIcon size="small"/> :
                                                                  <img className="thumbnail-img"
                                                                       width={10}
                                                                       src={avatarSrc}
                                                                       alt="avatar"/>
                                                              }
                                                              <span
                                                                  className="font-weight-500">{report.patient.firstName + ' ' + report.patient.lastName}</span>
                                                          </div>
                                                      );
                                                  }}
                                    />
                                    <Table.Column key="date" title={<FormattedMessage id="date"/>}
                                                  sorter={reports.length ?
                                                      (reportOne: Report, reportTwo: Report) => reportOne.date?.localeCompare(reportTwo.date!)!
                                                      : undefined}
                                                  width="10%"
                                                  render={(report) => dayjs(report.date).utc().format('DD.MM.YYYY')}
                                    />
                                    <Table.Column key="condition" title={<FormattedMessage id="condition"/>}
                                                  width="10%"
                                                  render={(report) => report.conditionDisplayName || statusesMap.get(report.conditionId)?.displayName}
                                    />
                                    <Table.Column key="treatment" title={<FormattedMessage id="treatment"/>}
                                                  width="20%"
                                                  render={(report) => report.treatmentDisplayName || treatmentsMap.get(report.treatmentId)?.displayName}
                                    />
                                    <Table.Column key="tooth" title={<FormattedMessage id="tooth"/>}
                                                  width="5%"
                                                  render={(report) => report.toothNumber}
                                    />
                                    <Table.Column key="price" title={<FormattedMessage id="price"/>}
                                                  width="5%"
                                                  render={(report) => report.price.toFixed(2)}
                                    />
                                    <Table.Column key="debt" title={<FormattedMessage id="debt_label"/>}
                                                  width="5%"
                                                  render={(report) => report.debt.toFixed(2)}
                                    />
                                    <Table.Column key="paid" title={<FormattedMessage id="paidFinance"/>}
                                                  width="5%"
                                                  render={(report) => report.paid.toFixed(2)}
                                    />
                                    <Table.Column key="doctor" title={<FormattedMessage id="doctor_label"/>}
                                                  width="10%"
                                                  onCell={(report: Report) => {
                                                      return {
                                                          onClick: (e) => {
                                                              e.stopPropagation();

                                                              navigate("/medical/staff/" + report.doctorId)
                                                          }
                                                      };
                                                  }}
                                                  render={(report) => {
                                                      const doctor = medicalStaffMap.get(report.doctorId);
                                                      const doctorInitials = doctor?.firstName && doctor?.lastName
                                                          ? doctor.firstName[0] + '. ' + doctor.lastName[0] + '.'
                                                          : report.doctorDisplayName ?? '—';
                                                      return <Link to={"/medical/staff/" + (doctor?.id ?? report.doctorId)}>
                                                          {doctorInitials}
                                                      </Link>;
                                                  }}
                                    />
                                </Table>
                            </InfiniteScroll>
                        </div>
                    </>
                )}
                <div id="bottom-div" style={{bottom: 0, position: 'absolute'}}></div>
            </DetailWrapper>
        </AccessControl>
    )
};

export default Reports;